// src/api/aiNotesToContent.js
const API_BASE = '/api';

const AI_NOTES_API_URL = `${API_BASE}/compose-content`;


/**
 * Send notes to AI with streaming response
 * @param {Array} stickies - Array of sticky note objects
 * @param {string} task - The AI task/prompt
 * @param {Function} onChunk - Callback for each streaming chunk
 * @param {Function} onComplete - Callback when streaming is complete
 * @param {Function} onError - Callback for errors
 * @returns {Promise<string>} - Final accumulated content
 * 
 * 
 * 
 */

export const sendStickySubmenuToAI = async (stickies, task) => {
  try {
    const payload = {
      stickies: stickies.map((sticky, index) => ({
        id: sticky.id,
        type: sticky.type,
        content: sticky.content,
        displayNumber: index + 1
      })),
      task
    };

    const response = await fetch(`${API_BASE}/sticky-submenu-ai`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      // Create error with response info for debugging
      const error = new Error();
      error.response = { status: response.status, data: {} };
      
      // Set specific error messages
      if (response.status === 402) {
        error.message = "Insufficient token balance";
      } else if (response.status === 401) {
        error.message = "Authentication failed";
      } else if (response.status === 400) {
        error.message = "Invalid request";
      } else if (response.status === 429) {
        error.message = "Rate limit exceeded";
      } else if (response.status === 500) {
        error.message = "AI composition failed";
      } else {
        error.message = `HTTP ${response.status}: ${response.statusText}`;
      }
      
      throw error;
    }

    const result = await response.json();
    return result.output || "";
  } catch (error) {
    throw error;
  }
};

export const sendNotesToAI = async (stickies, task, artToken = null, model = 'gpt4', onChunk = null, onComplete = null, onError = null) => {
  try {
    console.log('Sending streaming request with', stickies.length, 'stickies');
    console.log('Task:', task);
    console.log('ART Token:', artToken); // ADD THIS LOG

    // Make the fetch request
    const response = await fetch(AI_NOTES_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Include credentials for authentication
        'credentials': 'include'
      },
      credentials: 'include', // Important for authentication
      body: JSON.stringify({
        stickies: stickies,
        task: task,
        notes_already_formatted: true, // Since we're passing formatted task
        artToken: artToken,
        model: model
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('No response body available for streaming');
    }

    // Create a reader for the response stream
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let accumulatedContent = '';
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        ///console.log('[FRONTEND DEBUG] Raw chunk received:', done, value ? new Date().toISOString() : 'no data');
        if (done) {
          console.log('Stream completed');
          break;
        }

        // Decode the chunk and add to buffer
        const chunk = decoder.decode(value, { stream: true });
        ///console.log('[FRONTEND DEBUG] Decoded chunk:', chunk.length, 'chars at', new Date().toISOString());
        buffer += chunk;

        // Process complete SSE messages in buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const jsonStr = line.slice(6); // Remove 'data: ' prefix
              const data = JSON.parse(jsonStr);
              
              //console.log('Received chunk:', data);

              // Handle different types of messages
              if (data.error) {
                // Error message
                if (onError) {
                  onError(new Error(data.content || 'AI processing error'));
                }
                return data.content || 'Error occurred';
              }

              if (data.final) {
                // Final message
                accumulatedContent = data.accumulated_content || accumulatedContent;
                if (onComplete) {
                  onComplete({
                    content: accumulatedContent,
                    tokenUsage: data.token_usage || 0,
                    processingTime: data.processing_time_seconds || 0
                  });
                }
                console.log('Stream finished. Final content length:', accumulatedContent.length);
                return accumulatedContent;
              }

              // Regular content chunk
              if (data.content) {
                accumulatedContent = data.accumulated_content || accumulatedContent;
                
                if (onChunk) {
                  onChunk({
                    newContent: data.content,
                    accumulatedContent: accumulatedContent,
                    tokenUsage: data.token_usage || 0,
                    processingTime: data.processing_time_seconds || 0
                  });
                }
              }

            } catch (parseError) {
              console.warn('Failed to parse SSE data:', line, parseError);
            }
          }
        }
      }

      return accumulatedContent;

    } finally {
      reader.releaseLock();
    }

  } catch (error) {
    console.error('Streaming error:', error);
    if (onError) {
      onError(error);
    }
    throw error;
  }
};

// Legacy non-streaming function for backward compatibility
export const sendNotesToAILegacy = async (stickies, task) => {
  try {
    const response = await fetch(AI_NOTES_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        stickies: stickies,
        task: task,
        notes_already_formatted: true
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result.output;
  } catch (error) {
    console.error('Error in legacy AI request:', error);
    throw error;
  }
};